/* eslint-disable no-case-declarations */
import { useReducer, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as ACT from './actions'
import { CompanyModal } from './CompanyModal'
import { makeGetRequest } from 'utils/api'
import { toSelectValues } from './serializers'

const initialCompany = {
  name: '',
  legalName: '',
  description: '',
  address: '',
  address2: '',
  street2: '',
  city: '',
  state: '',
  country: '',
  zipcode: '',
  phone: '',
  fax: '',
  website: '',
  email: '',
  emailDomain: '',
  types: [],
  size: '',
  preQualified: false,
  preQualifiedExpDate: '',
  facebook: '',
  linkedin: '',
  twitter: '',
  instagram: '',
  youtube: '',
  file: null,
  noBidReasonId: null,
  segment: null,
  parentCompany: null,
  noBidReasonDescription: '',
  noBid: false
}

const requiredFields = ['name', 'legalName']

const reducer = (state, action) => {
  switch (action.type) {
    case ACT.CHANGE_COMPANY_FIELD:
      const { property, value } = action.payload
      return { ...state, [property]: value }
    case ACT.RESET_COMPANY:
      return { ...initialCompany }
    case ACT.SET_COMPANY:
      const { company } = action.payload
      return { ...company, noBidReasonId: company.noBidReason?.id }
    default:
      throw new Error()
  }
}

const CompanyModalContainer = ({ toggleCompanyModal, showCompanyModal, company, actionType }) => {
  const [hasErrors, setHasErrors] = useState(false)
  const [file, setFile] = useState(null)
  const [editedCompany, dispatchState] = useReducer(reducer, initialCompany)
  const [noBidReasons, setNoBidReasons] = useState([])
  const [segments, setSegments] = useState([])
  const [hasDuplicateError, setHasDuplicateError] = useState(false)
  const [companyTypes, setCompanyTypes] = useState([])

  const dispatch = useDispatch()

  useEffect(() => {
    if (!showCompanyModal) {
      clearCompany()
    }
  }, [showCompanyModal])

  useEffect(() => {
    if (company?.name) {
      dispatchState({
        type: ACT.SET_COMPANY,
        payload: {
          company
        }
      })
    }
  }, [company])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/companies/nobidreasons')
      setNoBidReasons(toSelectValues(response))
      const segments = await makeGetRequest('/v1/market-segments')
      setSegments(toSelectValues(segments))
      const types = await makeGetRequest('/v1/company-type')
      setCompanyTypes(toSelectValues(types))
    })()
  }, [])

  const changeProperty = (value, property) => {
    dispatchState({
      type: ACT.CHANGE_COMPANY_FIELD,
      payload: {
        property,
        value
      }
    })
  }

  const validateRequired = cb => {
    const emptyRequiredFields = requiredFields.filter(field => !editedCompany[field])
    const error = !!emptyRequiredFields.length
    setHasErrors(error)
    if (!error) {
      cb()
    }
  }

  const clearCompany = () => {
    dispatchState({
      type: ACT.RESET_COMPANY
    })
    setFile(null)
    setHasErrors(false)
    setHasDuplicateError(false)
  }

  const onSave = () => {
    validateRequired(() => {
      if (!hasErrors) {
        dispatch({
          type: actionType,
          payload: {
            company: editedCompany,
            file,
            id: editedCompany.id
          }
        })
        toggleCompanyModal()
      }
    })
  }

  const uploadFile = e => {
    const file = e.target.files[0]

    const fr = new FileReader()
    fr.onload = () => {
      setFile(file)
    }
    if (file) {
      fr.readAsDataURL(file)
    }
  }

  const onAddressSelectCallback = params => {
    const { address, city, state, zipCode, country, street } = params

    dispatchState({
      type: ACT.SET_COMPANY,
      payload: {
        company: {
          ...editedCompany,
          address,
          address2: street,
          city,
          state,
          country,
          zipcode: zipCode
        }
      }
    })
  }

  const validateProperty = (value, suggestions) => {
    const error = !!suggestions.filter(
      suggestion => suggestion.title.trim().toLowerCase() === value.trim().toLowerCase()
    ).length
    setHasDuplicateError(error)
  }

  return (
    <CompanyModal
      company={editedCompany}
      showCompanyModal={showCompanyModal}
      toggleCompanyModal={toggleCompanyModal}
      changeProperty={changeProperty}
      saveCompany={onSave}
      showCompanyError={hasErrors}
      uploadFile={uploadFile}
      onAddressSelectCallback={onAddressSelectCallback}
      noBidReasons={noBidReasons}
      segments={segments}
      validateProperty={validateProperty}
      showDuplicateError={hasDuplicateError}
      companyTypes={companyTypes}
    />
  )
}

export default CompanyModalContainer
